import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
// import { Button } from '@progress/kendo-react-buttons'
import { Checkbox, MaskedTextBox } from '@progress/kendo-react-inputs'
import { useAppDispatch } from 'app/hooks'
import { useLazyGetSystemQuery, useUpdateSystemSettingsMutation } from 'app/services/system.service'
import { systemActions } from 'app/slices/system.slice'
import { BUTTONS, EXPLANATIONS, FORM, INFORMATION, TABLE, TITLES } from 'locales/locales'
import Loading from 'components/spinner/Loading'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import CustomIcon from 'components/icon/CustomIcon'
import { icons } from 'utils/icons'
import { FixMeLater } from 'types'
import CustomButton from 'components/button/CustomButton'
import CustomText from 'components/text/CustomText'
import { mobileVersionValidation } from 'utils/helpers/validations'

interface SystemSettings {
  features: {
    newsFeedEditAndDeleteEnabled: boolean
    videoCallEnabled: boolean
    createTeam: boolean
    createChannel: boolean
    showPhoneInfo: boolean
  }
}

interface FeatureCheckboxProps {
  title: string
  featureKey: keyof SystemSettings['features']
  label: string
  explanation: string
}

const AdminMobileAppSettings: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [updateSystem] = useUpdateSystemSettingsMutation()
  const [getSystemSettings] = useLazyGetSystemQuery()

  const [systemSettingId, setSystemSettingId] = useState('')
  const [systemFeatures, setSystemFeatures] = useState<SystemSettings['features']>({
    newsFeedEditAndDeleteEnabled: false,
    videoCallEnabled: false,
    createTeam: true,
    createChannel: true,
    showPhoneInfo: false,
  })
  const [iosVersion, setIosVersion] = useState('')
  const [androidVersion, setAndroidVersion] = useState('')
  const [isValidIosVersion, setIsValidIosVersion] = useState({isValid: true, errorText: ""})
  const [isValidAndroidVersion, setIsValidAndroidVersion] = useState({isValid: true, errorText: ""})

  const [initialFeatures, setInitialFeatures] = useState<any>(null)


  useEffect(() => {
    fetchSystemSettings()
  }, [])

  const fetchSystemSettings = async () => {
    try {
      Loading.ref.show()
      const response = await getSystemSettings({})
      if (response?.data?.data && Object.keys(response.data.data).length !== 0) {
        const features = response.data.data.features
        const { iosVersion = '0.0.0', androidVersion = '0.0.0' } = response.data.data
        setInitialFeatures(features)
        setSystemFeatures({
          newsFeedEditAndDeleteEnabled: features.newsFeedEditAndDeleteEnabled,
          videoCallEnabled: features.videoCallEnabled,
          createTeam: features.createTeam,
          createChannel: features.createChannel,
          showPhoneInfo: features.showPhoneInfo,
        })
        setIosVersion(iosVersion)
        setAndroidVersion(androidVersion)
        setSystemSettingId(response.data.data._id)
        dispatch(systemActions.updateSystem(response.data.data))
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }

  const handleFeatureToggle = (featureKey: keyof SystemSettings['features']) => {
    setSystemFeatures((prevFeatures) => ({
      ...prevFeatures,
      [featureKey]: !(prevFeatures?.[featureKey] ?? false),
    }))
  }

  const handleInputChange = (value: string, key: string) => {
    if (key === 'iosVersion') {
      setIosVersion(value)
    } else if (key === 'androidVersion') {
      setAndroidVersion(value)
    }
  }

  const FeatureCheckbox: React.FC<FeatureCheckboxProps> = ({
    title,
    featureKey,
    label,
    explanation,
  }) => (
    <FeatureCard>
      <FeatureCardHeader>
        <FeatureTitle>{title}</FeatureTitle>
        <Tooltip
          content={explanation}
          position="top"
          openDelay={0}
          anchorElement="target"
          parentTitle={false}
        >
          <InfoIconWrapper>
            <CustomIcon source={icons.infoCircleIcon} iconPreset="medium" title={explanation} />
          </InfoIconWrapper>
        </Tooltip>
      </FeatureCardHeader>
      <FeatureCardBody>
        <FeatureToggleContainer>
          <StyledCheckbox
            checked={systemFeatures?.[featureKey] ?? false}
            onChange={() => handleFeatureToggle(featureKey)}
          />
          <ToggleLabel>{systemFeatures?.[featureKey] ? t(TABLE.ACTIVE) : t(TABLE.INACTIVE)}</ToggleLabel>
        </FeatureToggleContainer>
        <FeatureLabel>{label}</FeatureLabel>
      </FeatureCardBody>
    </FeatureCard>
  )

  const handleSubmit = async () => {
    try {
      Loading.ref.show()
      const iosVersionValidation = mobileVersionValidation(iosVersion)
      const androidVersionValidation = mobileVersionValidation(androidVersion)
      setIsValidIosVersion(iosVersionValidation)
      setIsValidAndroidVersion(androidVersionValidation)
      if (systemSettingId && iosVersionValidation.isValid && androidVersionValidation.isValid) {
        const system: FixMeLater = await updateSystem({
          features: { ...initialFeatures, ...systemFeatures },
          _id: systemSettingId,
          iosVersion,
          androidVersion,
        })

        if (system?.data?.success) {
          dispatch(systemActions.updateSystemFeatures(systemFeatures))
        }
      }
    } catch (error) {
      console.error('Error updating system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }

  return (
    <ContentCard>
      <ContentCardHeader>{t(TITLES.MOBILE_APP_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <TwoColumnContainer>
          <ColumnContainer>
            <FeaturesGrid>
              <FeatureCheckbox
                title={t(FORM.VIDEO_CALL_ENABLE)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_VIDEO_CALL_CHECKBOX)}
                featureKey="videoCallEnabled"
                label="IOS & ANDROID"
              />
              <FeatureCheckbox
                title={t(FORM.NEWS_FEED_DELETE_EDIT)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_EDIT_REMOVE_NEWSFEED_CHECKBOX)}
                featureKey="newsFeedEditAndDeleteEnabled"
                label="IOS & ANDROID"
              />
              <FeatureCheckbox
                title={t(FORM.SHOW_PHONE_INFO)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_SHOW_PHONE_INFO)}
                featureKey="showPhoneInfo"
                label="IOS & ANDROID"
              />
              <FeatureCheckbox
                title={t(FORM.CREATE_TEAM_ENABLED)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CREATE_TEAM_CHECKBOX)}
                featureKey="createTeam"
                label="IOS & ANDROID"
              />
              <FeatureCheckbox
                title={t(FORM.CREATE_CHANNEL_ENABLED)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CREATE_CHANNEL_CHECKBOX)}
                featureKey="createChannel"
                label="IOS & ANDROID"
              />
              <FeatureCardVersionWrapper>
                <VersionInputContainer>
                  <CustomText typography="header3">{t(FORM.IOS_VERSION) as string}</CustomText>
                  <MaskedTextBox
                    width="100%"
                    mask="0.0.0"
                    defaultValue={iosVersion}
                    title={t(FORM.IOS_VERSION) as string}
                    onChange={(e) => handleInputChange(e.target.value, 'iosVersion')}
                    valid={isValidIosVersion?.isValid}
                    validationMessage={isValidIosVersion?.errorText || ""}
                  />
                </VersionInputContainer>
                <VersionInputContainer>
                  <CustomText typography="header3">{t(FORM.ANDROID_VERSION) as string}</CustomText>
                  <MaskedTextBox
                    width="100%"
                    mask="0.0.0"
                    defaultValue={androidVersion}
                    valid={isValidAndroidVersion?.isValid}
                    validationMessage={isValidAndroidVersion?.errorText || ""}
                    onChange={(e) => handleInputChange(e.target.value, 'androidVersion')}
                  />
                </VersionInputContainer>
              </FeatureCardVersionWrapper>
            </FeaturesGrid>
            <ButtonContainer>
              <CustomStyledButton text={t(BUTTONS.SAVE)} onClick={handleSubmit} />
            </ButtonContainer>
          </ColumnContainer>
          <ColumnContainer>
            <InfoSection>
              <InfoSectionHeader>{t(INFORMATION.FEATURE_MANAGEMENT_INFO)}</InfoSectionHeader>
              <InfoSectionContent>
                <InfoItem>
                  <InfoIcon source={icons.infoCircleIcon} iconPreset="small" />
                  <InfoText>{t(INFORMATION.MOBILE_APP_SETTINGS_EXPLANATION)}</InfoText>
                </InfoItem>
                <InfoItem>
                  ✓<InfoText>{t(INFORMATION.MOBILE_APP_SETTINGS_ACTIVE_INFO)}</InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon source={icons.closeIcon} iconPreset="small" />
                  <InfoText>{t(INFORMATION.MOBILE_APP_SETTINGS_PASSIVE_INFO)}</InfoText>
                </InfoItem>
              </InfoSectionContent>
            </InfoSection>
          </ColumnContainer>
        </TwoColumnContainer>
      </ContentCardBody>
    </ContentCard>
  )
}

export default AdminMobileAppSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const FeatureCard = styled.div`
  background: white;
  border: 1px solid #e8f4f8;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: #3151b7;
  }
`
const FeatureCardVersionWrapper = styled(FeatureCard)`
  display: flex;
  flex-direction: row;
  gap: 15px;
`

const VersionInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

const FeatureCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`

const FeatureTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  flex: 1;
  line-height: 1.5;
`

const InfoIconWrapper = styled.div`
  cursor: pointer;
  border-radius: 100%;
  background-color: #f7fafc;
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #edf2f7;
    transform: scale(1.1);
  }
`

const FeatureCardBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FeatureToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
`

const FeatureLabel = styled.span`
  background: linear-gradient(135deg, #3151b7 0%, #4c63d2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(49, 81, 183, 0.2);
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
`

const StyledCheckbox = styled(Checkbox)`
  .k-checkbox {
    width: 24px;
    height: 24px;
    border: 2px solid #cbd5e0;
    border-radius: 6px;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      border-color: #3151b7;
      transform: scale(1.05);
    }
  }

  .k-checkbox:checked {
    background: linear-gradient(135deg, #3151b7 0%, #4c63d2 100%);
    border-color: #3151b7;
    box-shadow: 0 2px 8px rgba(49, 81, 183, 0.3);

    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 14px;
      font-weight: bold;
    }
  }

  .k-checkbox:focus {
    outline: 2px solid rgba(49, 81, 183, 0.2);
    outline-offset: 2px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 32px;
  width: 100%;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 24px;
  }
`

const ColumnContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &:first-child {
    flex: 2;
  }

  &:last-child {
    flex: 1;
    min-width: 300px;
  }
`

const CustomStyledButton = styled(CustomButton)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: #1f4ab7;
  margin-left: auto;
`

const InfoSection = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
`

const InfoSectionHeader = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const InfoSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #3151b7;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`

const InfoIcon = styled(CustomIcon)`
  margin-top: 2px;
  flex-shrink: 0;
`

const InfoText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #4a5568;
`
